import { getAuth } from "firebase/auth";
import { cleanMetadata, cleanText } from "./errorSanitization";

export type BrowserErrorSeverity = "warning" | "error" | "fatal";

export interface BrowserErrorContext {
  operation: string;
  severity?: BrowserErrorSeverity;
  route?: string;
  metadata?: Record<string, unknown>;
}

const reportedObjects = new WeakSet<object>();

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    const withCode = error as Error & { code?: unknown };
    return {
      name: cleanText(error.name || "Error", 120),
      message: cleanText(error.message),
      stack: error.stack ? cleanText(error.stack, 12_000) : undefined,
      code:
        withCode.code === undefined
          ? undefined
          : cleanText(withCode.code, 200),
    };
  }

  if (typeof error === "object" && error !== null) {
    const value = error as Record<string, unknown>;
    return {
      name: cleanText(value.name ?? "NonErrorThrown", 120),
      message: cleanText(value.message ?? value.error ?? "Unknown object error"),
      code: value.code === undefined ? undefined : cleanText(value.code, 200),
    };
  }

  return { name: "NonErrorThrown", message: cleanText(error) };
}

function errorEndpoint() {
  const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
  return baseUrl ? `${baseUrl}/errors/client` : null;
}

export async function reportError(
  error: unknown,
  context: BrowserErrorContext
): Promise<void> {
  if (typeof error === "object" && error !== null) {
    if (reportedObjects.has(error)) return;
    reportedObjects.add(error);
  }

  const endpoint = errorEndpoint();
  if (!endpoint) {
    console.error("[error-reporting] REACT_APP_API_URL is not configured", error);
    return;
  }

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const currentUser = getAuth().currentUser;
    if (currentUser) {
      headers.Authorization = `Bearer ${await currentUser.getIdToken()}`;
    }
  } catch {
    // Auth failures are precisely one of the cases this endpoint must accept.
  }

  const payload = {
    ...normalizeError(error),
    operation: cleanText(context.operation, 240),
    severity: context.severity ?? "error",
    route: cleanText(context.route ?? window.location.pathname, 500),
    metadata: context.metadata ? cleanMetadata(context.metadata) : undefined,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!response.ok) {
      console.error(`[error-reporting] Endpoint returned ${response.status}`);
    }
  } catch (reportingError) {
    // Terminal fallback only; never recursively report reporter failures.
    console.error("[error-reporting] Request failed", reportingError);
  }
}

export function installGlobalErrorHandlers() {
  window.addEventListener("error", (event) => {
    void reportError(event.error ?? event.message, {
      operation: "browser.window.error",
      severity: "fatal",
      metadata: {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    void reportError(event.reason, {
      operation: "browser.promise.unhandledRejection",
      severity: "fatal",
    });
  });
}
